import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import EfImgVideo from '../ef-img-video';
import EfLink from '../ef-link';
import './index.scss';

interface EfSectionProps {
    /**
     * 资源链接是否是视频
     */
    isVideo?: boolean;
    /**
     * loop
     */
    loop?: boolean;
    /**
     * autoPlay
     */
    autoPlay?: boolean;
    /**
     * startPlay
     */
    startPlay?: boolean;
    /**
     * 所有分辨率展示的资源链接
     */
    pc?: string;
    /**
     * 分辨率等于1024px展示的资源链接
     */
    pad?: string;
    /**
     * 分辨率小于等于1024px展示的资源链接
     */
    h5?: string;
    /**
     * 视频贴图-所有分辨率
     */
    poster?: string;
    /**
     * 视频贴图-分辨率小于等于1024px
     */
    posterH5?: string;

    /**
     * 主题色
     */
    theme?: 'black' | 'white';

    /**
     * 是否开启进场动画
     */
    hasAnimation?: boolean;

    /**
     *
     * 进场动画是否已启动
     */
    animationEnterCallback?: () => void;

    /**
     * 文案展示位置
     */
    position?: 'default' | 'hover' | 'hoverL' | 'hoverR';

    /**
     * label(处于标题之上的叫label)
     */
    label?: string;
    /**
     * 主标题
     */
    title?: string;
    /**
     * 副标题
     */
    subTitle?: string;
    /**
     * 描述
     */
    desc?: string;
    /**
     * tip
     */
    tip?: string;
    /**
     * 样式名
     */
    className?: string;

    /**
     * 更多详情-链接
     */
    moreLink?: string;
    /**
     * 更多详情-文案
     */
    moreText?: string;
    /**
     * 埋点-更多按钮
     */
    moreDataLayer?: any[];

    /**
     * 购买按钮-链接
     */
    buyLink?: string;
    /**
     * 购买按钮-文案
     */
    buyText?: string;
    /**
     * 埋点-购买按钮
     */
    buyDataLayer?: any[];

    /**
     * 图片上 fixed 的文案 or icon
     */
    fixedElement?: any;

    /**
     * 视频下面的重新播放 icon
     */
    Icon?: any;
}

/**
 * 维护:谢保林
 *
 * 段落
 */
const EfSection: React.FC<EfSectionProps> = ({
    isVideo,
    loop,
    autoPlay,
    startPlay,
    pc,
    pad,
    h5,
    poster,
    posterH5,
    theme = 'black',
    position,
    hasAnimation = true,
    animationEnterCallback,
    label,
    title,
    subTitle,
    desc,
    tip,
    className,
    moreLink,
    moreText,
    moreDataLayer,
    buyLink,
    buyText,
    buyDataLayer,
    fixedElement,
    Icon,
}) => {
    const refSection = useRef(null);
    const [isEnter, setIsEnter] = useState(false);
    const prefixCls = `ef-section`;
    const classes = classNames(prefixCls, {
        [`${className}`]: className,
        [`${prefixCls}-position-${position}`]: position,
        [`${prefixCls}-theme-${theme}`]: theme,
        [`${prefixCls}-animation`]: hasAnimation,
        [`${prefixCls}-enter`]: isEnter,
    });
    // 是否有按钮
    const hasBtn = (moreLink && moreText) || (buyLink && buyText);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (elems: any) => {
                const elem = elems[0];

                const topNum = elem.boundingClientRect.top;

                if (elem.isIntersecting && topNum > 0) {
                    setIsEnter(true);
                    animationEnterCallback && animationEnterCallback();
                }
            },
            {
                rootMargin: `0px 0px -100px 0px`,
                threshold: 0,
            },
        );
        observer.observe(refSection.current as any);
    }, []);

    // 埋点1
    let moreDataLayer2 = null;
    if (moreDataLayer) {
        moreDataLayer2 = moreDataLayer;
    } else {
        moreDataLayer2 = [
            {
                event: 'uaEvent',
                event_name: 'activity_preview',
                button_name: moreText,
            },
        ];
    }
    // 埋点2
    let buyDataLayer2 = null;
    if (buyDataLayer) {
        buyDataLayer2 = buyDataLayer;
    } else {
        buyDataLayer2 = [
            {
                event: 'uaEvent',
                event_name: 'activity_preview',
                button_name: moreText,
            },
        ];
    }

    return (
        <div className={classes} ref={refSection}>
            <div className={`${prefixCls}-info`}>
                <div className={`${prefixCls}-msg`}>
                    {label && <p className={`${prefixCls}-label`}>{label}</p>}
                    {title && <h2 className={`${prefixCls}-title`}>{title}</h2>}
                    {subTitle && <p className={`${prefixCls}-subTitle`}>{subTitle}</p>}
                    {desc && <div className={`${prefixCls}-desc`}>{desc}</div>}
                    {tip && <div className={`${prefixCls}-tip`}>{tip}</div>}
                </div>
                {hasBtn && (
                    <div className={`${prefixCls}-btns`}>
                        {moreLink && moreText && (
                            <EfLink href={moreLink} className={`${prefixCls}-link_more`} dataLayer={moreDataLayer2}>
                                {moreText}
                            </EfLink>
                        )}
                        {buyLink && buyText && (
                            <EfLink href={buyLink} className={`${prefixCls}-buy_now`} dataLayer={buyDataLayer2}>
                                {buyText}
                            </EfLink>
                        )}
                    </div>
                )}
            </div>
            {pc && (
                <div className={`${prefixCls}-img`}>
                    <EfImgVideo
                        isVideo={isVideo}
                        pc={pc}
                        pad={pad || ''}
                        h5={h5 || pc}
                        poster={poster}
                        posterH5={posterH5 || poster}
                        autoPlay={String(autoPlay) ? autoPlay : true}
                        loop={String(loop) ? loop : true}
                        startPlay={startPlay}
                        Icon={Icon}
                    />
                    {!!fixedElement && <div className={`${prefixCls}-fixed-element`}>{fixedElement}</div>}
                </div>
            )}
        </div>
    );
};

export default EfSection;
